import React from 'react'
import LecturesSubNav from '../components/LecturesSubNav'
import LectureSettingsTipsCard from './components/LectureSettingsTipsCard'
import useLectureSettings from './hooks/useLectureSettings'

export default function SettingsScreen() {
  const {
    settings,
    isSaving,
    setField,
    toggleSmsTo,
    toggleWhatsAppTo,
    handleSave,
    handleReset,
  } = useLectureSettings()

  const RECIPIENT_OPTIONS = [
    { key: 'students', label: 'Students', icon: 'school' },
    { key: 'parents', label: 'Father / Parents', icon: 'person' },
    { key: 'mother', label: 'Mother', icon: 'favorite' },
    { key: 'faculty', label: 'Faculty / Staff', icon: 'co_present' },
  ]

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Canopy: Header & SubNav ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Administration &amp; Tools</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Lecture Settings &amp; Automation
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Configure attendance population rules, automated SMS &amp; WhatsApp notifications, and biometric tolerances.
          </p>
        </div>
      </section>

      {/* ── 2-Column Layout: Settings Form + Tips ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Settings Configuration Card (7 Cols) */}
        <div className="flex flex-col gap-6 lg:col-span-7">
          <div className="flex flex-col gap-6 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 sm:p-8 shadow-xs">
            {/* Section 1: Attendance & Student Enrollment */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <span className="material-symbols-outlined text-base">how_to_reg</span>
                <span>Attendance &amp; Student Enrollment</span>
              </div>

              {/* Populate Students */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Populate Students in Attendance
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setField('populateStudents', 'S')}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 text-left transition-all cursor-pointer ${settings.populateStudents === 'S'
                        ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                        : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                      }`}
                  >
                    <span className="material-symbols-outlined text-lg">filter_alt</span>
                    <div>
                      <p className="text-xs font-bold text-on-surface">Selected Subjects Only</p>
                      <p className="text-[10px] text-on-surface-variant">Filters enrolled subject students</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setField('populateStudents', 'A')}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 text-left transition-all cursor-pointer ${settings.populateStudents === 'A'
                        ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                        : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                      }`}
                  >
                    <span className="material-symbols-outlined text-lg">group</span>
                    <div>
                      <p className="text-xs font-bold text-on-surface">All Students</p>
                      <p className="text-[10px] text-on-surface-variant">Shows all batch students</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Repeat Lectures */}
              <div className="flex flex-col gap-2 mt-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Repeat Lectures (Multi-day Topic Attendance)
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'Y', label: 'Yes (Repeat Mode)', icon: 'check_circle' },
                    { value: 'N', label: 'No (Standard Mode)', icon: 'cancel' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setField('repeatLectures', opt.value)}
                      className={`flex-1 flex items-center justify-center gap-2 rounded-2xl border py-2.5 px-4 text-xs font-bold transition-all cursor-pointer ${settings.repeatLectures === opt.value
                          ? 'border-primary bg-primary/10 text-primary shadow-xs'
                          : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                        }`}
                    >
                      <span className="material-symbols-outlined text-base">{opt.icon}</span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Section 2: SMS Configuration */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <span className="material-symbols-outlined text-base">sms</span>
                <span>SMS Notification Policies</span>
              </div>

              {/* SMS Delivery Schedule */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                    SMS Delivery Schedule
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'M', label: 'Manual' },
                      { value: 'A', label: 'Automatic' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setField('smsSchedule', opt.value)}
                        className={`flex-1 rounded-xl border py-2 px-3 text-xs font-bold transition cursor-pointer ${settings.smsSchedule === opt.value
                            ? 'border-primary bg-primary text-on-primary shadow-xs'
                            : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* SMS Format Type */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                    SMS Format Type
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'C', label: 'Combined' },
                      { value: 'S', label: 'Separate' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setField('smsType', opt.value)}
                        className={`flex-1 rounded-xl border py-2 px-3 text-xs font-bold transition cursor-pointer ${settings.smsType === opt.value
                            ? 'border-primary bg-primary text-on-primary shadow-xs'
                            : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* SMS Recipients */}
              <div className="flex flex-col gap-2 mt-1">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  SMS Recipients (Send To)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {RECIPIENT_OPTIONS.map((rec) => {
                    const active = Boolean(settings.smsTo[rec.key])
                    return (
                      <button
                        key={rec.key}
                        type="button"
                        onClick={() => toggleSmsTo(rec.key)}
                        className={`flex items-center gap-2 rounded-2xl border p-2.5 text-xs font-bold transition-all cursor-pointer ${active
                            ? 'border-primary/40 bg-primary/10 text-primary shadow-2xs'
                            : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                          }`}
                      >
                        <span className="material-symbols-outlined text-sm">
                          {active ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                        <span className="truncate">{rec.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Section 3: WhatsApp Configuration */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                <span className="material-symbols-outlined text-base">chat</span>
                <span>WhatsApp Notification Policies</span>
              </div>

              {/* WhatsApp Recipients */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  WhatsApp Recipients (Send To)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {RECIPIENT_OPTIONS.map((rec) => {
                    const active = Boolean(settings.whatsAppTo[rec.key])
                    return (
                      <button
                        key={rec.key}
                        type="button"
                        onClick={() => toggleWhatsAppTo(rec.key)}
                        className={`flex items-center gap-2 rounded-2xl border p-2.5 text-xs font-bold transition-all cursor-pointer ${active
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 shadow-2xs'
                            : 'border-outline-variant/25 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                          }`}
                      >
                        <span className="material-symbols-outlined text-sm">
                          {active ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                        <span className="truncate">{rec.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Section 4: Biometric Swipe Tolerance Window */}
            <div className="flex flex-col gap-4 border-b border-outline-variant/15 pb-6">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <span className="material-symbols-outlined text-base">fingerprint</span>
                <span>Biometric Swipe Attendance Tolerance Window</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Start Swipe Time */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                    Start Swipe Tolerance
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={settings.swipeTimeBefore}
                      onChange={(e) => setField('swipeTimeBefore', parseInt(e.target.value, 10) || 0)}
                      className="h-10 w-24 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-center text-sm font-bold text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    />
                    <span className="text-xs font-medium text-on-surface-variant">
                      minutes before start
                    </span>
                  </div>
                </div>

                {/* End Swipe Time */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
                    End Swipe Tolerance
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={settings.swipeTimeAfter}
                      onChange={(e) => setField('swipeTimeAfter', parseInt(e.target.value, 10) || 0)}
                      className="h-10 w-24 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-center text-sm font-bold text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    />
                    <span className="text-xs font-medium text-on-surface-variant">
                      minutes after start
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-2 rounded-2xl border border-outline-variant/30 bg-surface-container-low px-5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition cursor-pointer"
              >
                <span className="material-symbols-outlined text-base">restart_alt</span>
                <span>Reset to Defaults</span>
              </button>

              <button
                type="button"
                disabled={isSaving}
                onClick={handleSave}
                className="flex items-center gap-2 rounded-2xl bg-primary px-7 py-2.5 text-xs font-extrabold text-on-primary shadow-md hover:bg-primary/90 transition cursor-pointer disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-base">
                  {isSaving ? 'progress_activity' : 'save'}
                </span>
                <span>{isSaving ? 'Saving Settings...' : 'Save Settings'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Tips & Guidelines (5 Cols) */}
        <div className="lg:col-span-5">
          <LectureSettingsTipsCard />
        </div>
      </div>
    </div>
  )
}
